import { RteColorPicker as NativeColorPicker } from '@extensions/components/Form/RteColorPicker';
import { IconFont } from '@bay-email/editor';
import React, { useMemo } from 'react';
import { ToolItem } from '../../ToolItem';
import { findDynamicColorKey } from '../dynamicColorVar';
import { t } from '@bay-email/core';

interface BrandColor {
	label: string;
	color: string;
	value: string;
}

export function IconBgColor({
	selectionRange,
	execCommand,
	getPopoverMountNode,
	onBeforeInteract,
	onCommit,
	brandColors,
}: {
	selectionRange: Range | null;
	execCommand: (cmd: string, val?: any, opts?: { dynamicTag?: string }) => void;
	getPopoverMountNode: () => HTMLElement;
	onBeforeInteract?: () => void;
	onCommit?: () => void;
	brandColors?: BrandColor[];
}) {
	const color = useMemo(() => {
		if (!selectionRange) return undefined;

		let element: HTMLElement | null = null;

		// Get the starting element
		if (selectionRange.commonAncestorContainer instanceof HTMLElement) {
			element = selectionRange.commonAncestorContainer;
		} else if (selectionRange.commonAncestorContainer.parentNode instanceof HTMLElement) {
			element = selectionRange.commonAncestorContainer.parentNode;
		}

		// Prefer the dynamic brand-colour merge-tag KEY recorded on the wrapper (stored
		// brace-less) so the picker re-opens in Dynamic mode and remembers the brand
		// selection. Re-wrap it as "{{...}}" to match the brand swatch values.
		const dynamicKey = element
			? findDynamicColorKey(selectionRange, element, 'data-bay-background-color-var')
			: null;
		if (dynamicKey) return `{{${dynamicKey}}}`;

		// Traverse up the DOM tree to find background color
		while (element) {
			const style = getComputedStyle(element);
			const bgColor = style.backgroundColor;

			// Check if this element has a meaningful background color (not transparent/rgba(0,0,0,0))
			if (bgColor && bgColor !== 'rgba(0, 0, 0, 0)' && bgColor !== 'transparent') {
				return bgColor;
			}

			element = element.parentElement;
		}

		return undefined;
	}, [selectionRange]);

	// The toolbar icon's underline needs a real colour; when `color` is a brand merge
	// tag, show the brand's resolved hex instead of the (uncolourable) tag string.
	const displayColor = useMemo(
		() => brandColors?.find(b => b.value === color)?.color ?? color,
		[brandColors, color],
	);

	return (
		<NativeColorPicker
			value={color}
			brandColors={brandColors}
			supportsDynamic
			onChange={nextColor => {
				const brand = brandColors?.find(b => b.value === nextColor);
				if (brand) {
					execCommand('hiliteColor', brand.color, { dynamicTag: brand.value });
				} else {
					execCommand('hiliteColor', nextColor);
				}
				// Re-snapshot after each execCommand: the DOM mutated (new span wrapping),
				// so lastValidRangeRef must be updated to the post-mutation selection.
				onBeforeInteract?.();
			}}
			onDynamicCommit={(brandValue, brandHex) => {
				// Dynamic mode: apply the brand's resolved hex as the visible colour and
				// carry the merge tag so the dynamic substitution happens at send time.
				execCommand('hiliteColor', brandHex, { dynamicTag: brandValue });
				onBeforeInteract?.();
			}}
			onCommit={onCommit}
			getPopupContainer={getPopoverMountNode}
			highlightRange={selectionRange}
			showInput={false}
			onBeforeOpen={onBeforeInteract}
		>
			<ToolItem
				icon={
					<div style={{ position: 'relative' }}>
						<IconFont
							size={12}
							iconName='icon-bg-color'
							style={{ position: 'relative', top: '-1px' }}
						/>
						<div
							style={{
								borderBottom: `2px solid ${displayColor}`,
								position: 'absolute',
								width: '130%',
								left: '-15%',
								top: 16,
							}}
						/>
					</div>
				}
				title={t('Background Color')}
			/>
		</NativeColorPicker>
	);
}
